import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Button } from 'antd';
import { CloseCircle, LogoutCurve, NotificationBing } from 'iconsax-react';
import { Logout, NotificationList } from '@src/pages/Dashboard';
import { ResponsiveModal } from '@src/components/ResponsiveModal';
import clsx from 'clsx';
import { ThemeToggle } from '@src/components/ThemeToggle';
import { useSelector } from 'react-redux';
import { selectLayout, THEME } from '@src/store/layout/layoutSlice';
import { WhiteLabelLogo } from '@src/components';

type RightSidebarProps = {
  currentUserEmail?: string;
  onClose?: () => void;
};

export function RightSidebar({ currentUserEmail, onClose }: RightSidebarProps) {
  const { t } = useTranslation();
  const layout = useSelector(selectLayout);

  const [notificationOpen, setNotificationOpen] = useState(false);
  const [confirmLogOutOpen, setConfirmLogOutOpen] = useState(false);

  const handleCloseNotification = () => {
    setTimeout(() => {
      setNotificationOpen(false);
    }, 300);
  };

  const handleCloseLogOutConfirm = () => {
    setTimeout(() => {
      setConfirmLogOutOpen(false);
    }, 300);
  };

  return (
    <>
      <div className='w-full h-full overflow-auto flex flex-col bg-wl-background'>
        <div className='flex items-center justify-between px-8 mt-8 mb-16 text-xl font-bold'>
          <div className='flex items-center space-x-1'>
            <WhiteLabelLogo />
          </div>
          <CloseCircle
            className='md:hidden text-wl-on-background'
            size={32}
            variant='Bulk'
            onClick={onClose}
          />
        </div>

        <div className='italic text-gray-400 flex justify-center mt-8 px-6 mb-6'>
          {currentUserEmail}
        </div>
        <div className='flex justify-center px-6 mb-6'>
          <Button
            className='w-full justify-start border-0 bg-gray-200 text-black hover:!bg-gray-200 hover:!text-black h-16 rounded-xl'
            variant='outlined'
            onClick={() => setNotificationOpen(true)}
          >
            <NotificationBing
              className='cursor-pointer'
              size={32}
              variant='Bulk'
            />
            {t('buttons.notifications')}
          </Button>
        </div>
        <div className='absolute w-full bottom-6 justify-center px-6 shadow-[0_-4px_10px_rgba(0,0,0,0.1)]'>
          <div className='flex items-center justify-between rounded-xl my-4 p-4 font-normal text-gray-600 bg-gray-200 dark:bg-black dark:text-white'>
            <div>
              {layout.theme === THEME.DARK ? 'Dark Mode' : 'Light Mode'}
            </div>
            <div className='flex items-center space-x-1'>
              <ThemeToggle />
            </div>
          </div>
          <Button
            className={clsx(
              'justify-start !h-auto w-full !px-5 !py-4 !bg-transparent !rounded-xl',
              'text-error-light border-error-light hover:!text-error hover:!border-error'
            )}
            variant='outlined'
            onClick={() => setConfirmLogOutOpen(true)}
          >
            <LogoutCurve size='24' variant='Bulk' />
            {t('buttons.signOut')}
          </Button>
        </div>
      </div>
      <ResponsiveModal
        isOpen={confirmLogOutOpen}
        title={t('pages.auth.signOut.title')}
        headerIcon={<LogoutCurve variant='Bulk' />}
        content={<Logout onClose={handleCloseLogOutConfirm} />}
        onClose={handleCloseLogOutConfirm}
      />

      <ResponsiveModal
        isOpen={notificationOpen}
        title={t('pages.notification.title')}
        headerIcon={<NotificationBing variant='Bulk' />}
        content={<NotificationList />}
        onClose={handleCloseNotification}
      />
    </>
  );
}
